<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { FileText } from 'lucide-vue-next'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import RelativeTime from '@/components/RelativeTime.vue'
import StatusBadge from '@/components/applications/StatusBadge.vue'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { formatAmount, formatLabel } from '@/lib/utils'
import { portalCustomerApplicationPath } from '@/routes'

interface Application {
  id: string
  status: string
  loan_type: string
  purpose: string
  amount: number
  created_at: string
}

defineProps<{ applications: Application[] }>()

const tableColumns: TableColumn[] = [
  { key: 'purpose', label: 'Purpose' },
  { key: 'amount', label: 'Amount' },
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
  { key: 'link', label: '', align: 'right' },
]
</script>

<template>
  <Head title="My Applications" />
  <PortalLayout actor-type="customer">
    <Card>
      <CardHeader>
        <div class="flex items-center gap-3">
          <FileText class="text-muted-foreground size-5" />
          <div>
            <CardTitle class="text-base">My Applications</CardTitle>
            <CardDescription>{{ applications.length }} application{{ applications.length !== 1 ? 's' : '' }}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent class="p-0">
        <div v-if="applications.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
          <FileText class="text-muted-foreground/40 size-10" />
          <p class="text-muted-foreground text-sm">No applications yet</p>
        </div>
        <AppDataTable v-else :columns="tableColumns">
          <tr v-for="app in applications" :key="app.id" class="border-border hover:bg-muted/50 border-b last:border-0 transition-colors">
            <td class="px-6 py-4 font-medium">{{ app.purpose ? formatLabel(app.purpose) : formatLabel(app.loan_type) }}</td>
            <td class="text-muted-foreground px-6 py-4">{{ formatAmount(app.amount) }}</td>
            <td class="px-6 py-4"><StatusBadge :status="app.status" /></td>
            <td class="text-muted-foreground px-6 py-4"><RelativeTime :date="app.created_at" /></td>
            <td class="px-6 py-4 text-right">
              <Link :href="portalCustomerApplicationPath(app.id)" class="text-primary text-xs hover:underline">Track →</Link>
            </td>
          </tr>
        </AppDataTable>
      </CardContent>
    </Card>
  </PortalLayout>
</template>
